/**
 * @fileoverview Settings page / Profile Dashboard
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { apiService } from '@/services/api/apiService';
import clsx from 'clsx';

export const SettingsPage = () => {
  const { user, updateUser, login } = useAuth();
  const { t, languages } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || user?.first_name || '',
    lastName: user?.lastName || user?.last_name || '',
    email: user?.email || 'usuario@email.com',
    organizationName: user?.tenant?.name || 'Academia Vuelo Sur',
    newEmail: '',
    confirmEmail: '',
    preferredLanguage: user?.preferredLanguage || user?.preferred_language_code || 'es',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorToken: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPassword2FA, setShowPassword2FA] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatarUrl || null);
  const [selectedTimezone, setSelectedTimezone] = useState(user?.tenant?.timezone || 'UTC');
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);
  const { getCurrentTime, getTimezoneOffset } = useDateFormat();

  const API_URL = import.meta.env.VITE_API_BASE_URL || '/api';
  const BASE_URL = window.location.origin;


  // Sync timezone when user data changes
  useEffect(() => {
    if (user?.tenant?.timezone) {
      console.log('[SettingsPage] User timezone from backend:', user.tenant.timezone);
      setSelectedTimezone(user.tenant.timezone);
    }
  }, [user?.tenant?.timezone]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Local preview
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);

    setIsUploading(true);
    const uploadData = new FormData();
    uploadData.append('avatar', file);

    try {
      const response = await fetch(`${API_URL}/users/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: uploadData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || t('settings.error_upload', 'Error al subir imagen'));
      }

      const data = await response.json();

      // Update context and storage
      const updatedUser = { ...user, avatarUrl: data.avatarUrl };
      login(updatedUser, localStorage.getItem('auth_token'));
      setSuccess(t('settings.avatar_success', 'Foto de perfil actualizada con éxito'));
    } catch (err) {
      setError(err.message || t('settings.error_upload', 'Error al subir la imagen'));
      setAvatarPreview(user?.avatarUrl || null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      const data = await apiService.request('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          preferredLanguageCode: formData.preferredLanguage
        })
      });

      // Update local storage and context through login
      login({
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
        preferredLanguage: formData.preferredLanguage
      }, localStorage.getItem('auth_token'));

      setIsEditing(false);
      setSuccess(t('settings.profile_updated', 'Perfil actualizado correctamente'));
    } catch (err) {
      setError(err.message || t('settings.error_update', 'Error al actualizar perfil'));
      console.error(err);
    }
  };



  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('settings.password_mismatch', 'Las contraseñas no coinciden'));
      return;
    }

    setIsChangingPassword(true);
    setError('');
    setSuccess('');

    try {
      await apiService.request('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          twoFactorToken: passwordData.twoFactorToken
        })
      });

      setSuccess(t('settings.password_success', 'Contraseña actualizada correctamente'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorToken: ''
      });
      setShowPassword2FA(false);
    } catch (err) {
      if (err.twoFactorRequired) {
        setShowPassword2FA(true);
        setError(t('settings.2fa_required_password', 'Se requiere un código 2FA para cambiar la contraseña'));
      } else {
        setError(err.message || t('settings.password_error', 'Error al cambiar la contraseña'));
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      console.log('[2FA SETUP FRONTEND] Initiating for user:', user?.email);
      setError('');
      const data = await apiService.request('/two-factor/setup', { method: 'POST' });
      console.log('[2FA SETUP FRONTEND] Received secret for:', user?.email);
      setTwoFactorData(data);
      setShow2FASetup(true);
    } catch (err) {
      setError(t('settings.error_2fa', 'Error al iniciar configuración 2FA'));
      console.error(err);
    }
  };

  const handleVerifyRedirect = () => {
    sessionStorage.setItem('is2FASetup', 'true');
    navigate('/otp');
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-[#192A42] tracking-tight">{t('settings.title', 'Configuración de Cuenta')}</h1>
        <p className="mt-2 text-slate-500 text-lg">{t('settings.subtitle', 'Gestiona tu información personal y configuración de cuenta')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-6 flex flex-col items-center text-center transition-all hover:shadow-lg">
            <div className="w-32 h-32 rounded-full bg-gradient-to-tr from-blue-500 to-blue-400 p-1 mb-4 shadow-xl relative group">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center border-4 border-white overflow-hidden relative">
                {avatarPreview ? (
                  <img src={avatarPreview.startsWith('data:') || avatarPreview.startsWith('blob:') ? avatarPreview : avatarPreview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-blue-600">
                    {user?.firstName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                )}

                {/* Upload Overlay */}
                <label className="absolute inset-0 bg-slate-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <i className={clsx("fas text-white text-2xl", isUploading ? "fa-spinner fa-spin" : "fa-camera")}></i>
                  <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} disabled={isUploading} />
                </label>
              </div>
              <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 border-4 border-white rounded-full shadow-sm"></div>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
              {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username}
            </h2>
            <p className="text-slate-500 font-medium mb-4">{user?.email}</p>
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-wide">
              <i className="fas fa-shield-alt mr-2"></i>
              {user?.isSuperAdmin ? t('settings.super_admin', 'Super Administrador') : user?.roles?.[0]?.role_display_name || t('settings.user', 'Usuario')}
            </span>
          </div>
        </div>

        {/* Right Column: Edit Forms */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-8 border-t-4 border-t-blue-500 transition-all hover:shadow-lg">

            <h3 className="text-xl font-bold text-slate-800 mb-8 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                <i className="fas fa-sliders-h text-xl"></i>
              </div>
              {t('settings.account_settings', 'Configuración de Cuenta')}
            </h3>

            {/* Section 1: Personal Information */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-bold text-slate-700 flex items-center gap-2">
                  <i className="fas fa-user-circle text-blue-500"></i>
                  {t('settings.personal_info', 'Información Personal')}
                </h4>
                <button
                  onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                  className={clsx(
                    "px-3 py-1.5 rounded-lg font-semibold text-xs transition-all flex items-center gap-2",
                    isEditing
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  )}
                >
                  <i className={clsx("fas", isEditing ? "fa-check" : "fa-pen")}></i>
                  {isEditing ? t('common.save', 'Guardar') : t('common.edit', 'Editar')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.first_name', 'Nombre')}</label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all disabled:opacity-75 disabled:cursor-not-allowed disabled:bg-slate-100"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.last_name', 'Apellido')}</label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all disabled:opacity-75 disabled:cursor-not-allowed disabled:bg-slate-100"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.preferred_language', 'Idioma Preferido')}</label>
                  <select
                    disabled={!isEditing}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all disabled:opacity-75 disabled:cursor-not-allowed disabled:bg-slate-100 appearance-none"
                    value={formData.preferredLanguage}
                    onChange={(e) => setFormData({ ...formData, preferredLanguage: e.target.value })}
                  >
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.native_name || lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <hr className="border-slate-100 my-8" />

            {/* Section 2: Organization */}
            <div className="mb-8">
              <h4 className="text-lg font-bold text-slate-700 flex items-center gap-2 mb-4">
                <i className="fas fa-building text-blue-500"></i>
                {t('settings.organization', 'Organización')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">{t('settings.org_name', 'Nombre')}</label>
                  <p className="text-base font-semibold text-[#192A42]">{user?.tenant?.name || t('settings.not_assigned', 'No asignado')}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">{t('settings.entity_type', 'Tipo de Entidad')}</label>
                  <p className="text-base font-semibold text-[#192A42] truncate">
                    {user?.tenant?.organization_type?.replace(/_/g, ' ') || t('settings.default_entity_type', 'Centro de Instrucción de Aeronáutica Civil')}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">{t('settings.certifier_id', 'ID de Certificador')}</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono text-[#0077B6] bg-blue-50 px-2 py-1 rounded border border-blue-100">
                      {user?.tenant?.blockchain_wallet_address || '0x' + (user?.tenant?.id?.substring(0, 8) || '...') + '...'}
                    </code>
                  </div>
                </div>

                {/* Timezone Selector */}
                <div className="md:col-span-1 mt-3 pt-3 border-t border-slate-100">
                  <label className="block text-sm font-semibold text-[#192A42] mb-2">
                    <i className="fas fa-clock text-blue-500 mr-2"></i>
                    {t('settings.timezone', 'Timezone')}
                  </label>
                  <select
                    className="w-full max-w-md px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all appearance-none"
                    value={selectedTimezone}
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                  >
                    <option value="UTC">{t('timezone.utc', 'UTC (Coordinated Universal Time)')} +00:00</option>
                    <optgroup label={t('timezone.americas', '🌎 Americas')}>
                      <option value="America/New_York">{t('timezone.new_york', 'New York')} (EST/EDT) UTC-05:00/-04:00</option>
                      <option value="America/Chicago">{t('timezone.chicago', 'Chicago')} (CST/CDT) UTC-06:00/-05:00</option>
                      <option value="America/Denver">{t('timezone.denver', 'Denver')} (MST/MDT) UTC-07:00/-06:00</option>
                      <option value="America/Los_Angeles">{t('timezone.los_angeles', 'Los Angeles')} (PST/PDT) UTC-08:00/-07:00</option>
                      <option value="America/Mexico_City">{t('timezone.mexico_city', 'Mexico City')} UTC-06:00/-05:00</option>
                      <option value="America/Bogota">{t('timezone.bogota', 'Bogota')} UTC-05:00</option>
                      <option value="America/Lima">{t('timezone.lima', 'Lima')} UTC-05:00</option>
                      <option value="America/Santiago">{t('timezone.santiago', 'Santiago')} UTC-04:00/-03:00</option>
                      <option value="America/Argentina/Buenos_Aires">{t('timezone.buenos_aires', 'Buenos Aires')} UTC-03:00</option>
                      <option value="America/Sao_Paulo">{t('timezone.sao_paulo', 'Sao Paulo')} UTC-03:00</option>
                    </optgroup>
                    <optgroup label={t('timezone.europe', '🌍 Europe')}>
                      <option value="Europe/London">{t('timezone.london', 'London')} (GMT/BST) UTC+00:00/+01:00</option>
                      <option value="Europe/Paris">{t('timezone.paris', 'Paris')} (CET/CEST) UTC+01:00/+02:00</option>
                      <option value="Europe/Berlin">{t('timezone.berlin', 'Berlin')} (CET/CEST) UTC+01:00/+02:00</option>
                      <option value="Europe/Madrid">{t('timezone.madrid', 'Madrid')} (CET/CEST) UTC+01:00/+02:00</option>
                      <option value="Europe/Rome">{t('timezone.rome', 'Rome')} (CET/CEST) UTC+01:00/+02:00</option>
                      <option value="Europe/Amsterdam">{t('timezone.amsterdam', 'Amsterdam')} (CET/CEST) UTC+01:00/+02:00</option>
                      <option value="Europe/Zurich">{t('timezone.zurich', 'Zurich')} (CET/CEST) UTC+01:00/+02:00</option>
                      <option value="Europe/Moscow">{t('timezone.moscow', 'Moscow')} (MSK) UTC+03:00</option>
                    </optgroup>
                    <optgroup label={t('timezone.asia_pacific', '🌏 Asia/Pacific')}>
                      <option value="Asia/Dubai">{t('timezone.dubai', 'Dubai')} (GST) UTC+04:00</option>
                      <option value="Asia/Kolkata">{t('timezone.kolkata', 'Mumbai/Kolkata')} (IST) UTC+05:30</option>
                      <option value="Asia/Singapore">{t('timezone.singapore', 'Singapore')} (SGT) UTC+08:00</option>
                      <option value="Asia/Hong_Kong">{t('timezone.hong_kong', 'Hong Kong')} (HKT) UTC+08:00</option>
                      <option value="Asia/Tokyo">{t('timezone.tokyo', 'Tokyo')} (JST) UTC+09:00</option>
                      <option value="Asia/Shanghai">{t('timezone.shanghai', 'Shanghai')} (CST) UTC+08:00</option>
                      <option value="Australia/Sydney">{t('timezone.sydney', 'Sydney')} (AEDT/AEST) UTC+11:00/+10:00</option>
                      <option value="Pacific/Auckland">{t('timezone.auckland', 'Auckland')} (NZDT/NZST) UTC+13:00/+12:00</option>
                    </optgroup>
                  </select>

                  {/* Current time preview */}
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      <i className="fas fa-info-circle mr-1"></i>
                      {t('settings.current_time', 'Current time in your zone')}:
                    </span>
                    <span className="font-mono font-semibold text-blue-600">
                      {getCurrentTime()} ({getTimezoneOffset()})
                    </span>
                  </div>

                  {/* Save button */}
                  {selectedTimezone !== (user?.tenant?.timezone || 'UTC') && (
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={async () => {
                          setIsSavingTimezone(true);
                          setError('');
                          setSuccess('');
                          try {
                            await apiService.request(`/tenants/${user.tenant.id}`, {
                              method: 'PUT',
                              body: JSON.stringify({ timezone: selectedTimezone })
                            });
                            // Update user context + localStorage immediately
                            // This triggers re-render of all useDateFormat hooks across the portal
                            updateUser({
                              tenant: { ...user.tenant, timezone: selectedTimezone }
                            });
                            setSuccess(t('settings.timezone_updated', 'Zona horaria actualizada correctamente'));
                          } catch (err) {
                            setError(err.message || t('settings.timezone_error', 'Error al actualizar zona horaria'));
                          } finally {
                            setIsSavingTimezone(false);
                          }
                        }}
                        disabled={isSavingTimezone}
                        className="px-6 py-2.5 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition-all shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSavingTimezone ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            {t('common.saving', 'Guardando...')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check"></i>
                            {t('settings.save_timezone', 'Guardar Zona Horaria')}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <hr className="border-slate-100 my-8" />

            {/* Section 3: Change Email */}
            <div className="mb-8">
              <h4 className="text-lg font-bold text-slate-700 flex items-center gap-2 mb-4">
                <i className="fas fa-envelope text-blue-500"></i>
                {t('settings.change_email', 'Cambiar Email')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.new_email', 'Nuevo Email')}</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
                    placeholder="nuevo@email.com"
                    value={formData.newEmail}
                    onChange={(e) => setFormData({ ...formData, newEmail: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.confirm_email', 'Confirmar Email')}</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
                    placeholder="nuevo@email.com"
                    value={formData.confirmEmail}
                    onChange={(e) => setFormData({ ...formData, confirmEmail: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => alert(t('settings.email_change_requested', 'Cambio de email solicitado'))}
                  className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all shadow-md flex items-center gap-2"
                >
                  <i className="fas fa-sync-alt"></i>
                  {t('settings.update_email', 'Actualizar Email')}
                </button>
              </div>
            </div>

            <hr className="border-slate-100 my-8" />

            {/* Section 4: Security (2FA) */}
            <div>
              <h4 className="text-lg font-bold text-slate-700 flex items-center gap-2 mb-4">
                <i className="fas fa-shield-alt text-blue-500"></i>
                {t('settings.account_security', 'Seguridad de la Cuenta')}
              </h4>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm flex items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-100 text-green-600 rounded-lg text-sm flex items-center gap-2">
                  <i className="fas fa-check-circle"></i>
                  {success}
                </div>
              )}

              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={clsx("w-12 h-12 rounded-xl shadow-sm flex items-center justify-center border text-xl",
                      user?.twoFactorEnabled ? "bg-green-50 text-green-600 border-green-100" : "bg-white text-blue-600 border-slate-100"
                    )}>
                      <i className={clsx("fas", user?.twoFactorEnabled ? "fa-shield-check" : "fa-key")}></i>
                    </div>
                    <div>
                      <h5 className="font-bold text-[#192A42] flex items-center gap-2">
                        Google Authenticator (2FA)
                        {user?.twoFactorEnabled ? (
                          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold border border-green-200">
                            {t('common.active', 'Activado')}
                          </span>
                        ) : user?.hasPending2FA ? (
                          <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold border border-yellow-200">
                            {t('common.pending', 'Pendiente')}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold border border-red-200">
                            {t('common.not_active', 'No Activado')}
                          </span>
                        )}
                      </h5>
                      <p className="text-sm text-slate-500">{t('settings.2fa_desc', 'Protege tu cuenta con códigos de 6 dígitos')}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {user?.twoFactorEnabled ? (
                      <button
                        onClick={async () => {
                          if (!confirm(t('settings.confirm_disable_2fa', '¿Estás seguro de desactivar el 2FA? Tu cuenta será menos segura.'))) return;

                          try {
                            setError('');
                            setSuccess('');
                            await apiService.request('/two-factor/disable', { method: 'POST' });

                            // Update context
                            const updatedUser = { ...user, twoFactorEnabled: false };
                            login(updatedUser, localStorage.getItem('auth_token'));

                            setSuccess(t('settings.2fa_disabled', '2FA desactivado correctamente'));
                          } catch (err) {
                            console.error(err);
                            setError(t('settings.error_disable_2fa', 'Error al desactivar 2FA'));
                          }
                        }}
                        className="px-4 py-2 rounded-xl bg-red-50 text-red-600 font-bold hover:bg-red-100 transition-all text-sm border border-red-100"
                      >
                        {t('settings.disable', 'Desactivar')}
                      </button>
                    ) : (
                      !show2FASetup && (
                        <button
                          onClick={handleSetup2FA}
                          className="px-6 py-2 rounded-xl bg-[#192A42] text-white font-bold hover:bg-slate-800 transition-all text-sm shadow-md"
                        >
                          {user?.hasPending2FA ? t('settings.continue_config', 'Continuar Configuración') : t('settings.configure', 'Configurar')}
                        </button>
                      )
                    )}
                  </div>
                </div>

                {show2FASetup && twoFactorData && (
                  <div className="mt-8 pt-8 border-t border-slate-200 animate-fadeIn space-y-6">
                    <p className="text-sm text-slate-600 font-medium">
                      {t('settings.2fa_step1', '1. Escanea este código QR con la aplicación **Google Authenticator**.')}
                    </p>
                    <div className="flex flex-col items-center bg-white p-6 rounded-2xl border border-slate-100 shadow-inner w-fit mx-auto">
                      <img src={twoFactorData.qrCode} alt="2FA QR Code" className="w-48 h-48 mb-4 shadow-sm" />
                      <div className="text-center">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{t('settings.secret_key', 'Tu Llave Secreta')}</p>
                        <code className="text-sm font-mono text-blue-600 bg-blue-50 px-3 py-1 rounded">{twoFactorData.secret}</code>
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 font-medium text-center">
                      {t('settings.2fa_step2', '2. Una vez escaneado, haz clic para verificar el código.')}
                    </p>

                    <div className="flex justify-center">
                      <button
                        onClick={handleVerifyRedirect}
                        className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-500/30 flex items-center gap-2 active:scale-95"
                      >
                        {t('settings.verify_code', 'Verificar Código')} <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <hr className="border-slate-100 my-8" />

              {/* Section 5: Change Password */}
              <div className="mb-8">
                <h4 className="text-lg font-bold text-slate-700 flex items-center gap-2 mb-4">
                  <i className="fas fa-lock text-blue-500"></i>
                  {t('settings.change_password', 'Cambiar Contraseña')}
                </h4>
                <form onSubmit={handleChangePassword} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.current_password', 'Contraseña Actual')}</label>
                      <input
                        type="password"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.new_password', 'Nueva Contraseña')}</label>
                      <input
                        type="password"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#192A42] mb-2">{t('settings.confirm_new_password', 'Confirmar Nueva Contraseña')}</label>
                      <input
                        type="password"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  {showPassword2FA && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl space-y-3 animate-fadeInUp">
                      <label className="block text-sm font-bold text-yellow-800 flex items-center gap-2">
                        <i className="fas fa-shield-check"></i>
                        {t('settings.enter_2fa_code', 'Ingresa tu código de Google Authenticator')}
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="000000"
                        className="w-full px-4 py-3 rounded-xl border border-yellow-300 bg-white text-slate-700 font-mono text-center text-xl tracking-widest focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        value={passwordData.twoFactorToken}
                        onChange={(e) => setPasswordData({ ...passwordData, twoFactorToken: e.target.value.replace(/\D/g, '') })}
                      />
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isChangingPassword}
                      className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
                    >
                      {isChangingPassword ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          {t('common.processing', 'Procesando...')}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-key"></i>
                          {t('settings.update_password', 'Actualizar Contraseña')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
